import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useGetClause, useCreateClause, useUpdateClause, useAddClauseVersion, getGetClauseQueryKey, getListClausesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/RichTextEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { ArrowLeft, Save, Eye, EyeOff, Plus, X, Wand2 } from "lucide-react";
import { ClauseMergeFieldPicker } from "@/components/ClauseMergeFieldPicker";
import { DraftingWizard } from "@/components/DraftingWizard";
import { useOrg } from "@/lib/orgContext";
import { useActivePersona } from "@/lib/activePersona";
import { useAuth } from "@clerk/react";
import { renderMergeFields, MERGE_FIELDS } from "@/lib/mergeFields";
import { sanitizeHtml } from "@/lib/sanitize";
import type { CreateClauseBodyCategory, UpdateClauseBodyCategory, ClauseConditionExpr as ApiClauseConditionExpr, CreateClauseBodyDocumentKind } from "@workspace/api-client-react";
import { ClauseConditionBuilder, type ClauseConditionExpr } from "@/components/ClauseConditionBuilder";

const CATEGORIES = [
  { value: "compensation", label: "Compensation" },
  { value: "termination", label: "Termination" },
  { value: "buyout", label: "Buyout" },
  { value: "nil", label: "NIL" },
  { value: "title_ix", label: "Title IX" },
  { value: "indemnification", label: "Indemnification" },
  { value: "governing_law", label: "Governing Law" },
  { value: "outside_income", label: "Outside Income" },
  { value: "general", label: "General" },
];

const SAMPLE_CONTEXT = MERGE_FIELDS.reduce<Record<string, string>>((acc, f) => {
  acc[f.key] = f.example;
  return acc;
}, {});

interface ClauseEditorProps {
  mode: "new" | "edit";
}

export default function ClauseEditor({ mode }: ClauseEditorProps) {
  const params = useParams<{ id?: string }>();
  const clauseId = params.id ? parseInt(params.id, 10) : null;
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: existingClause, isLoading: loadingClause } = useGetClause(clauseId!, {
    query: { enabled: mode === "edit" && !!clauseId, queryKey: getGetClauseQueryKey(clauseId!) },
  });

  const createClause = useCreateClause();
  const updateClause = useUpdateClause();
  const addVersion = useAddClauseVersion();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<CreateClauseBodyCategory>("general");
  // Tag a clause as belonging to either contracts, term sheets, or both.
  // Defaults to "contract" which preserves the historical behavior — every
  // existing clause was created before term sheets existed, so they all
  // remain contract-scoped unless an admin explicitly retags them.
  const [documentKind, setDocumentKind] = useState<CreateClauseBodyDocumentKind>("contract");
  const [jurisdiction, setJurisdiction] = useState("");
  const [approvingAttorney, setApprovingAttorney] = useState("");
  const [lastReviewedAt, setLastReviewedAt] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [body, setBody] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [addVersionMode, setAddVersionMode] = useState(false);
  const [newVersionBody, setNewVersionBody] = useState("");
  const [inclusionCondition, setInclusionCondition] = useState<ClauseConditionExpr>({ kind: "always" });
  const [inclusionRequired, setInclusionRequired] = useState(false);
  const bodyEditorRef = useRef<RichTextEditorHandle>(null);
  const newVersionEditorRef = useRef<RichTextEditorHandle>(null);

  // Task #555: Drafting Wizard entry point on the editor header. Gated on
  // Legal persona + per-member opt-in flag. We fetch the toggle state once
  // on mount; falls back to localStorage so attorneys see the button
  // immediately on a re-open even before the API responds.
  const { org } = useOrg();
  const { getToken } = useAuth();
  const { effective: effectivePersona } = useActivePersona(
    org?.personas ?? [],
    org?.lastActivePersona ?? null,
  );
  const wizardLocalKey = "zindeal.draftingWizardEnabled";
  const [wizardEnabled, setWizardEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(wizardLocalKey) === "true";
  });
  const [wizardOpen, setWizardOpen] = useState(false);
  useEffect(() => {
    if (effectivePersona !== "Legal") return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch("/api/auth/drafting-wizard-prefs", {
          headers: token ? { authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok || cancelled) return;
        const j = (await r.json()) as { enabled?: boolean };
        const en = Boolean(j.enabled);
        setWizardEnabled(en);
        try { window.localStorage.setItem(wizardLocalKey, en ? "true" : "false"); } catch { /* ignore */ }
      } catch {
        /* fail-open: button stays based on localStorage */
      }
    })();
    return () => { cancelled = true; };
  }, [effectivePersona, getToken]);

  useEffect(() => {
    if (existingClause) {
      setTitle(existingClause.title);
      setCategory(existingClause.category);
      setJurisdiction(existingClause.jurisdiction ?? "");
      setApprovingAttorney(existingClause.approvingAttorney ?? "");
      setLastReviewedAt(existingClause.lastReviewedAt ?? "");
      setTags((existingClause.tags as string[]) ?? []);
      setBody(existingClause.latestVersion?.body ?? "");
      const existingKind = (existingClause as { documentKind?: CreateClauseBodyDocumentKind }).documentKind;
      if (existingKind) setDocumentKind(existingKind);
      if (existingClause.inclusionCondition) {
        setInclusionCondition(existingClause.inclusionCondition as ClauseConditionExpr);
      }
      setInclusionRequired(existingClause.inclusionRequired);
    }
  }, [existingClause]);

  const handleInsertMergeField = (field: string, target: "body" | "newVersion") => {
    if (target === "body") {
      if (bodyEditorRef.current) {
        bodyEditorRef.current.insertText(field);
      } else {
        setBody((prev) => prev + field);
      }
    } else {
      if (newVersionEditorRef.current) {
        newVersionEditorRef.current.insertText(field);
      } else {
        setNewVersionBody((prev) => prev + field);
      }
    }
  };

  const isBodyEmpty = (html: string) => {
    const stripped = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    return stripped.length === 0;
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));

  const handleSave = async () => {
    if (!title.trim() || isBodyEmpty(body)) {
      toast({ title: "Validation error", description: "Title and clause body are required.", variant: "destructive" });
      return;
    }

    try {
      if (mode === "new") {
        const clause = await createClause.mutateAsync({
          data: {
            title,
            category,
            documentKind,
            jurisdiction: jurisdiction || null,
            approvingAttorney: approvingAttorney || null,
            lastReviewedAt: lastReviewedAt || null,
            tags,
            body,
          },
        });
        // Persist inclusion condition immediately after creation (if one is set)
        if (inclusionCondition.kind !== "always" || inclusionRequired) {
          await updateClause.mutateAsync({
            id: clause.id,
            data: {
              inclusionCondition: inclusionCondition as ApiClauseConditionExpr,
              inclusionRequired,
            },
          });
          queryClient.invalidateQueries({ queryKey: getGetClauseQueryKey(clause.id) });
        }
        toast({ title: "Clause created", description: `"${title}" has been added to the library.` });
        queryClient.invalidateQueries({ queryKey: getListClausesQueryKey() });
        setLocation(`/clause-library/${clause.id}`);
      } else if (clauseId) {
        await updateClause.mutateAsync({
          id: clauseId,
          data: {
            title,
            category: category as UpdateClauseBodyCategory,
            documentKind,
            jurisdiction: jurisdiction || null,
            approvingAttorney: approvingAttorney || null,
            lastReviewedAt: lastReviewedAt || null,
            tags,
          },
        });
        toast({ title: "Clause updated", description: "Metadata has been saved." });
        queryClient.invalidateQueries({ queryKey: getGetClauseQueryKey(clauseId) });
        queryClient.invalidateQueries({ queryKey: getListClausesQueryKey() });
        setLocation(`/clause-library/${clauseId}`);
      }
    } catch (e) {
      handleApiError(toast, e, { fallbackTitle: "Could not save clause" });
    }
  };

  const handleSaveCondition = async () => {
    if (!clauseId) return;
    try {
      await updateClause.mutateAsync({
        id: clauseId,
        data: {
          inclusionCondition: inclusionCondition as ApiClauseConditionExpr,
          inclusionRequired,
        },
      });
      queryClient.invalidateQueries({ queryKey: getGetClauseQueryKey(clauseId) });
      toast({ title: "Condition saved", description: "Inclusion condition updated." });
    } catch (e) {
      handleApiError(toast, e, { fallbackTitle: "Could not save condition" });
    }
  };

  const handleAddVersion = async () => {
    if (!clauseId || isBodyEmpty(newVersionBody)) return;
    try {
      await addVersion.mutateAsync({ id: clauseId, data: { body: newVersionBody } });
      toast({ title: "New version added", description: "The clause has been versioned." });
      queryClient.invalidateQueries({ queryKey: getGetClauseQueryKey(clauseId) });
      queryClient.invalidateQueries({ queryKey: getListClausesQueryKey() });
      setAddVersionMode(false);
      setNewVersionBody("");
    } catch (e) {
      handleApiError(toast, e, { fallbackTitle: "Could not add version" });
    }
  };

  const isPending = createClause.isPending || updateClause.isPending;

  if (mode === "edit" && loadingClause) {
    return <div className="text-sm text-muted-foreground animate-pulse py-8">Loading clause…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href={clauseId ? `/clause-library/${clauseId}` : "/clause-library"}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {mode === "new" ? "New Clause" : "Edit Clause"}
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {mode === "new"
              ? "Create a reusable legal clause with merge field support."
              : "Update clause metadata or add a new version."}
          </p>
        </div>
        {effectivePersona === "Legal" && wizardEnabled && (
          <Button
            variant="outline"
            onClick={() => setWizardOpen(true)}
            data-testid="button-drafting-wizard"
          >
            <Wand2 className="h-4 w-4 mr-2" /> Drafting wizard
          </Button>
        )}
      </div>
      <DraftingWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onAccept={(skeletonText) => {
          // MVP insertion: drop the cited skeleton into whichever rich-text
          // surface is active (new-version draft if open, otherwise the
          // primary body). Tiptap citation overlay (footnote markers +
          // hover popover) is a deferred follow-up — see docs/CHANGELOG
          // entry for Task #555.
          const target = addVersionMode ? newVersionEditorRef.current : bodyEditorRef.current;
          if (target) {
            target.insertText(`\n\n${skeletonText}\n\n`);
          } else {
            // Fallback: append to state if the editor handle isn't ready.
            if (addVersionMode) setNewVersionBody((b) => `${b}\n\n${skeletonText}\n\n`);
            else setBody((b) => `${b}\n\n${skeletonText}\n\n`);
          }
          toast({ title: "Cited skeleton inserted", description: "Sources are listed at the bottom of the draft." });
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Clause Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Base Compensation Clause"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as CreateClauseBodyCategory)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="documentKind">Document Type *</Label>
              <Select value={documentKind} onValueChange={(v) => setDocumentKind(v as CreateClauseBodyDocumentKind)}>
                <SelectTrigger className="mt-1" data-testid="select-clause-document-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="term_sheet">Term Sheet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="jurisdiction">Jurisdiction</Label>
              <Input
                id="jurisdiction"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                placeholder="e.g. State of Ohio"
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="attorney">Approving Attorney</Label>
              <Input
                id="attorney"
                value={approvingAttorney}
                onChange={(e) => setApprovingAttorney(e.target.value)}
                placeholder="e.g. Jane Doe, Esq."
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="reviewed">Last Reviewed Date</Label>
              <Input
                id="reviewed"
                type="date"
                value={lastReviewedAt}
                onChange={(e) => setLastReviewedAt(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag and press Enter"
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="gap-1 pr-1">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="rounded hover:bg-muted p-0.5 ml-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Inclusion Condition</CardTitle>
            {mode === "edit" && (
              <Button variant="outline" size="sm" onClick={handleSaveCondition}>
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save condition
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ClauseConditionBuilder
            condition={inclusionCondition}
            required={inclusionRequired}
            onConditionChange={setInclusionCondition}
            onRequiredChange={setInclusionRequired}
          />
          {mode === "new" && (
            <p className="text-xs text-muted-foreground mt-3">
              The inclusion condition will be saved when you create the clause.
            </p>
          )}
        </CardContent>
      </Card>

      {mode === "edit" && addVersionMode ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Add New Version</CardTitle>
              <div className="flex gap-2">
                <ClauseMergeFieldPicker onInsert={(f) => handleInsertMergeField(f, "newVersion")} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview((p) => !p)}
                >
                  {showPreview ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {showPreview ? "Edit" : "Preview"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showPreview ? (
              <div
                className="min-h-[200px] p-3 rounded-md border bg-muted/20 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMergeFields(newVersionBody, SAMPLE_CONTEXT)) }}
              />
            ) : (
              <RichTextEditor
                ref={newVersionEditorRef}
                value={newVersionBody}
                onChange={setNewVersionBody}
                placeholder="Write the new version of this clause..."
                minHeight="200px"
              />
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAddVersionMode(false); setNewVersionBody(""); }}>Cancel</Button>
              <Button onClick={handleAddVersion} disabled={isBodyEmpty(newVersionBody) || addVersion.isPending}>
                {addVersion.isPending ? "Saving…" : "Save Version"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">
                {mode === "edit" ? "Clause Body (latest version)" : "Clause Body *"}
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                <ClauseMergeFieldPicker onInsert={(f) => handleInsertMergeField(f, "body")} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview((p) => !p)}
                >
                  {showPreview ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {showPreview ? "Edit" : "Preview"}
                </Button>
                {mode === "edit" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setAddVersionMode(true); setNewVersionBody(body); }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> New Version
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Use <code className="bg-muted px-1 rounded text-xs font-mono">{"{{field.key}}"}</code> syntax for merge fields. Click "Insert Merge Field" to browse available fields. Preview shows example values.
            </p>
            {showPreview ? (
              <div
                className="min-h-[200px] p-3 rounded-md border bg-muted/20 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMergeFields(body, SAMPLE_CONTEXT)) }}
              />
            ) : (
              <RichTextEditor
                ref={bodyEditorRef}
                value={body}
                onChange={setBody}
                placeholder="Write the clause body. Use {{coach.name}}, {{compensation.base_salary}}, etc. for merge fields..."
                disabled={mode === "edit"}
                minHeight="200px"
              />
            )}
            {mode === "edit" && (
              <p className="text-xs text-muted-foreground italic">
                To change the body, create a new version above. This preserves version history.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" asChild>
          <Link href={clauseId ? `/clause-library/${clauseId}` : "/clause-library"}>Cancel</Link>
        </Button>
        <Button onClick={handleSave} disabled={isPending}>
          <Save className="h-4 w-4 mr-2" />
          {isPending ? "Saving…" : mode === "new" ? "Create Clause" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
